import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Grid, Chip, CircularProgress,
  Alert, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, LinearProgress, Tabs, Tab,
  Divider, IconButton, Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AccountBalanceWallet as WalletIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import axios from 'axios';
import MilestonesTab from './MilestonesTab';
import { siteAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtShort = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
};

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];

const SiteDetailsPage = () => {
 const { siteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';

  const [site, setSite] = useState(null);
  const [activeTab, setActiveTab] = useState(parseInt(searchParams.get('tab') || '0'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Budget tracker state
  const [budgetData, setBudgetData] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [monthlySpending, setMonthlySpending] = useState([]);
  const [categorySpending, setCategorySpending] = useState([]);
  const [vendorSpending, setVendorSpending] = useState([]);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => { fetchSiteDetails(); }, [siteId]);
  useEffect(() => { if (activeTab === 1 && site) fetchBudgetData(); }, [activeTab, site]);
  useEffect(() => { if (activeTab === 2) fetchEntries(); }, [activeTab]);

  const fetchSiteDetails = async () => {
    try {
      setLoading(true);
      const [siteRes, spendingRes] = await Promise.all([
        siteAPI.getById(siteId),
        axios.get(`${API_BASE_URL}/materials/site/${siteId}`, authHeaders()).catch(() => null)
      ]);
      const siteData = siteRes.data.data.site;
      // Calculate actual spending from entries
      const entries = spendingRes?.data?.data?.entries || [];
      const actualSpending = entries
        .filter(e => e.entryType === 'received' && !e.deletedAt)
        .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
      setSite({ ...siteData, actualSpending });
    } catch (err) {
      setError('Failed to load site details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetData = useCallback(async () => {
    setBudgetLoading(true);
    try {
      // Get actual spending from material entries
      const [spendingRes, monthlyRes, categoryRes, vendorRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/reports/site-budget/${siteId}`, authHeaders()).catch(() => null),
        axios.get(`${API_BASE_URL}/reports/monthly-spending?siteId=${siteId}`, authHeaders()).catch(() => null),
        axios.get(`${API_BASE_URL}/reports/category-spending?siteId=${siteId}`, authHeaders()).catch(() => null),
        axios.get(`${API_BASE_URL}/materials/vendor-summary?siteId=${siteId}`, authHeaders()).catch(() => null),
      ]);

      // Calculate actual spending from material entries directly
      const entriesRes = await axios.get(`${API_BASE_URL}/materials/site/${siteId}`, authHeaders());
      const allEntries = entriesRes.data.data?.entries || [];
      const receivedEntries = allEntries.filter(e => e.entryType === 'received' && !e.deletedAt);
      const totalSpending = receivedEntries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

      const projectedBudget = parseFloat(site?.projectedBudget) || 0;
      const projectValue = parseFloat(site?.projectValue) || 0;
      const remaining = projectedBudget - totalSpending;
      const pct = projectedBudget > 0 ? (totalSpending / projectedBudget) * 100 : 0;
      const threshold = parseFloat(site?.budgetAlertThreshold) || 80;

      setBudgetData({
        projectedBudget,
        projectValue,
        totalSpending,
        remaining,
        pct,
        isOver: pct >= 100,
        isNear: pct >= threshold && pct < 100,
        threshold
      });

      // Monthly spending from entries
      const monthMap = {};
      receivedEntries.forEach(e => {
        const month = new Date(e.entryDate || e.createdAt).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
        monthMap[month] = (monthMap[month] || 0) + (parseFloat(e.amount) || 0);
      });
      const months = Object.entries(monthMap).map(([month, amount]) => ({ month, amount }));
      setMonthlySpending(months.slice(-12));

      // Category spending from entries
      const catMap = {};
      receivedEntries.forEach(e => {
        const cat = e.expenseCategory?.category_name || e.department?.department_name || 'Other';
        catMap[cat] = (catMap[cat] || 0) + (parseFloat(e.amount) || 0);
      });
      const cats = Object.entries(catMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
      setCategorySpending(cats);

      // Vendor spending
      const vendors = vendorRes?.data?.vendors || [];
      setVendorSpending(vendors.slice(0, 10));

    } catch (err) {
      console.error('Budget data error:', err);
    } finally {
      setBudgetLoading(false);
    }
  }, [siteId, site]);

  const fetchEntries = async () => {
    setEntriesLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/materials/site/${siteId}`, authHeaders());
      setEntries(res.data.data?.entries || []);
    } catch (e) { console.error(e); } finally { setEntriesLoading(false); }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/reports/site-report/${siteId}`, {
        ...authHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `${site?.siteName || 'Site'}_Budget_Report.xlsx`);
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      // Fallback: generate simple CSV from entries
      const received = entries.filter(e => e.entryType === 'received');
      const csv = [
        ['Date', 'Material', 'Category', 'Supplier', 'Bill No', 'Quantity', 'Unit', 'Amount'].join(','),
        ...received.map(e => [
          new Date(e.entryDate).toLocaleDateString('en-IN'),
          e.materialItem?.item_name || e.customItemDescription || '-',
          e.expenseCategory?.category_name || '-',
          e.supplierName || '-',
          e.billNumber || '-',
          e.quantity || '-',
          e.unit || '-',
          e.amount || 0
        ].join(','))
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `${site?.siteName || 'Site'}_Report.csv`);
      document.body.appendChild(a); a.click(); a.remove();
    } finally { setDownloading(false); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': case 'in_progress': return 'success';
      case 'completed': return 'info';
      case 'on_hold': case 'stopped': return 'warning';
      default: return 'default';
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (error || !site) return <Box><Alert severity="error">{error || 'Site not found'}</Alert><Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sites')} sx={{ mt: 2 }}>Back to Sites</Button></Box>;

  const statusColor = site.status === 'active' ? '#16a34a' : site.status === 'on_hold' ? '#d97706' : '#64748b';

  return (
    <Box sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');`}</style>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sites')} sx={{ mb: 1.5, textTransform: 'none', color: '#64748b' }}>
          Back to Sites
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '1.7rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{site.siteName}</Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mt: 0.3 }}>📍 {site.location} {site.projectCode && `· ${site.projectCode}`}</Typography>
          </Box>
          <Chip label={site.status?.replace('_', ' ').toUpperCase()} size="small"
            sx={{ background: `${statusColor}15`, color: statusColor, fontWeight: 700, border: `1px solid ${statusColor}30`, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.72rem' }} />
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', fontFamily: 'Plus Jakarta Sans, sans-serif' }, '& .Mui-selected': { color: '#2563eb' }, '& .MuiTabs-indicator': { backgroundColor: '#2563eb' } }}>
          <Tab label="Overview" />
          <Tab label="Budget Tracker" />
          <Tab label="Material Logs" />
          <Tab label="Team" />
          <Tab label="Milestones" />
        </Tabs>
      </Box>

      {/* ── Tab 0: Overview ── */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 2, color: '#0f172a' }}>Site Information</Typography>
              <Divider sx={{ mb: 2 }} />
              {[
                [<LocationIcon sx={{ fontSize: 18 }} />, 'Location', site.location],
                site.clientName && [<BusinessIcon sx={{ fontSize: 18 }} />, 'Client', site.clientName],
                site.projectCode && [null, 'Project Code', site.projectCode],
                site.startDate && [<CalendarIcon sx={{ fontSize: 18 }} />, 'Start Date', new Date(site.startDate).toLocaleDateString('en-IN')],
              ].filter(Boolean).map(([icon, label, value], i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  {icon && <Box sx={{ color: '#94a3b8', mt: 0.2 }}>{icon}</Box>}
                  <Box sx={{ ml: icon ? 0 : 3.5 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>{value}</Typography>
                  </Box>
                </Box>
              ))}
              {site.notes && (
                <Box sx={{ mt: 2, p: 2, background: '#f8fafc', borderRadius: '10px' }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, mb: 0.5 }}>NOTES</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>{site.notes}</Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            {parseFloat(site.projectedBudget) > 0 ? (
              <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WalletIcon sx={{ color: '#2563eb' }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>Budget Overview</Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => setActiveTab(1)}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600 }}>
                    Full Budget Tracker →
                  </Button>
                </Box>
                {(() => {
                  const spending = parseFloat(site.actualSpending) || 0;
                  const budget = parseFloat(site.projectedBudget) || 0;
                  const pct = budget > 0 ? (spending / budget) * 100 : 0;
                  const isOver = pct >= 100;
                  const isNear = pct >= (site.budgetAlertThreshold || 80) && !isOver;
                  const barColor = isOver ? '#dc2626' : isNear ? '#d97706' : '#16a34a';
                  return (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>Budget Utilization</Typography>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: barColor }}>{Math.min(pct, 9999).toFixed(1)}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.min(pct, 100)}
                          sx={{ height: 10, borderRadius: 5, background: '#f1f5f9', '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 5 } }} />
                        {isOver && <Typography sx={{ fontSize: '0.72rem', color: '#dc2626', mt: 0.5 }}>⚠️ Exceeded by {(pct - 100).toFixed(1)}%</Typography>}
                      </Box>
                      <Grid container spacing={2}>
                        {[
                          ['Project Value', fmtShort(site.projectValue), '#2563eb'],
                          ['Budget', fmtShort(budget), '#0891b2'],
                          ['Spent', fmtShort(spending), barColor],
                          ['Remaining', fmtShort(budget - spending), budget - spending < 0 ? '#dc2626' : '#16a34a'],
                        ].map(([label, value, color]) => (
                          <Grid item xs={6} sm={3} key={label}>
                            <Box sx={{ p: 2, background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>{label}</Typography>
                              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                      {isOver && <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>Budget exceeded! Go to Budget Tracker for full breakdown.</Alert>}
                      {isNear && <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>Approaching budget limit ({pct.toFixed(1)}% used).</Alert>}
                    </>
                  );
                })()}
              </Paper>
            ) : (
              <Paper sx={{ p: 4, borderRadius: '14px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                <WalletIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                <Typography sx={{ fontWeight: 700, color: '#64748b' }}>No Budget Set</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', mt: 0.5 }}>Edit the site to set a projected budget</Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {/* ── Tab 1: Budget Tracker ── */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>Budget Tracker — {site.siteName}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<RefreshIcon />} onClick={fetchBudgetData}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem' }}>
                Refresh
              </Button>
              <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadReport} disabled={downloading}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.78rem', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
                {downloading ? 'Downloading...' : 'Download Report'}
              </Button>
            </Box>
          </Box>

          {budgetLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : budgetData ? (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {[
                  { label: 'Project Value', value: fmtShort(budgetData.projectValue), color: '#2563eb', bg: '#eff6ff', icon: <BusinessIcon /> },
                  { label: 'Projected Budget', value: fmtShort(budgetData.projectedBudget), color: '#0891b2', bg: '#f0f9ff', icon: <WalletIcon /> },
                  { label: 'Total Spent', value: fmtShort(budgetData.totalSpending), color: budgetData.isOver ? '#dc2626' : '#d97706', bg: budgetData.isOver ? '#fef2f2' : '#fffbeb', icon: <TrendingUpIcon /> },
                  { label: 'Remaining', value: fmtShort(budgetData.remaining), color: budgetData.remaining < 0 ? '#dc2626' : '#16a34a', bg: budgetData.remaining < 0 ? '#fef2f2' : '#f0fdf4', icon: budgetData.remaining < 0 ? <WarningIcon /> : <CheckCircleIcon /> },
                ].map(({ label, value, color, bg, icon }) => (
                  <Grid item xs={6} md={3} key={label}>
                    <Box sx={{ p: 2.5, background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>{label}</Typography>
                          <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</Typography>
                        </Box>
                        <Box sx={{ p: 1, borderRadius: '10px', background: bg, color }}>{icon}</Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Progress */}
              <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>Budget Utilization</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: budgetData.isOver ? '#dc2626' : budgetData.isNear ? '#d97706' : '#16a34a' }}>
                    {budgetData.pct.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min(budgetData.pct, 100)}
                  sx={{ height: 12, borderRadius: 6, background: '#f1f5f9', '& .MuiLinearProgress-bar': { background: budgetData.isOver ? '#dc2626' : budgetData.isNear ? '#d97706' : '#16a34a', borderRadius: 6 } }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>₹0</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>Alert at {budgetData.threshold}%</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>{fmtShort(budgetData.projectedBudget)}</Typography>
                </Box>
                {budgetData.isOver && <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>🚨 Budget exceeded by {(budgetData.pct - 100).toFixed(1)}% — Immediate attention required</Alert>}
                {budgetData.isNear && <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>⚠️ {budgetData.pct.toFixed(1)}% of budget used — Approaching limit</Alert>}
              </Paper>

              {/* Charts Row */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Monthly Spending */}
                <Grid item xs={12} md={8}>
                  <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', height: 320 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 2 }}>Monthly Spending</Typography>
                    {monthlySpending.length === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No spending data yet</Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="85%">
                        <BarChart data={monthlySpending}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis dataKey="month" style={{ fontSize: '0.72rem' }} />
                          <YAxis tickFormatter={v => fmtShort(v)} style={{ fontSize: '0.72rem' }} />
                          <RechartsTooltip formatter={v => [fmtShort(v), 'Spending']} />
                          <Bar dataKey="amount" fill="#2563eb" radius={[6, 6, 0, 0]} name="Spending" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>

                {/* Category Breakdown */}
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', height: 380 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 2 }}>By Category</Typography>
                    {categorySpending.length === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>No data yet</Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="85%">
                        <PieChart>
                          <Pie data={categorySpending} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}>
                            {categorySpending.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <RechartsTooltip formatter={v => fmtShort(v)} />
                            <Legend iconSize={8} wrapperStyle={{ fontSize: '0.72rem' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {/* Vendor Spending */}
              {vendorSpending.length > 0 && (
                <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', mb: 3 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 2 }}>Top Vendors by Spending</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['Vendor', 'Invoices', 'Items', 'Total Spent'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', background: '#f8fafc' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vendorSpending.map((v, i) => (
                          <TableRow key={i} hover>
                            <TableCell><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{v.supplierName}</Typography></TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.82rem' }}>{v.invoiceCount}</Typography></TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.82rem' }}>{v.totalItems}</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#2563eb' }}>{fmtShort(v.totalSpent)}</Typography></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ color: '#94a3b8' }}>No budget data available. Set a projected budget for this site.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── Tab 2: Material Logs ── */}
      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>Material Entries — {site.siteName}</Typography>
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadReport} disabled={downloading}
              sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem' }}>
              Download Report
            </Button>
          </Box>
          {entriesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Date', 'Type', 'Material', 'Category', 'Supplier', 'Bill No', 'Qty', 'Amount'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', background: '#f8fafc' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: '#94a3b8' }}>No entries found</TableCell></TableRow>
                    ) : entries.map((e, i) => (
                      <TableRow key={e.id || i} hover>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{new Date(e.entryDate || e.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                        <TableCell>
                          <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: e.entryType === 'received' ? '#f0fdf4' : '#eff6ff', color: e.entryType === 'received' ? '#16a34a' : '#2563eb', border: `1px solid ${e.entryType === 'received' ? '#bbf7d0' : '#bfdbfe'}` }}>
                            {e.entryType === 'received' ? 'Received' : 'Used'}
                          </span>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', maxWidth: 160 }}>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }}>
                            {e.materialItem?.item_name || e.customItemDescription || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: '#64748b' }}>{e.expenseCategory?.category_name || e.department?.department_name || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{e.supplierName || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: '#64748b' }}>{e.billNumber || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{e.quantity ? `${e.quantity} ${e.unit || ''}` : '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: e.entryType === 'received' ? '#2563eb' : '#64748b' }}>
                          {e.amount ? `₹${Number(e.amount).toLocaleString('en-IN')}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {entries.length > 0 && (
                <Box sx={{ px: 3, py: 1.5, background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{entries.length} total entries</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#2563eb' }}>
                    Total: ₹{entries.filter(e => e.entryType === 'received').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0).toLocaleString('en-IN')}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* ── Tab 3: Team ── */}
      {activeTab === 3 && (
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', mb: 2 }}>Assigned Team</Typography>
          {site.assignedUsers && site.assignedUsers.length > 0 ? (
            <Grid container spacing={2}>
              {site.assignedUsers.map((u, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Box sx={{ p: 2.5, background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>{u.name?.charAt(0).toUpperCase()}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a' }}>{u.name}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{u.email}</Typography>
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{u.role}</span>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8, background: 'white', borderRadius: '14px', border: '2px dashed #e2e8f0' }}>
              <PersonIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
              <Typography sx={{ color: '#64748b', fontWeight: 600 }}>No supervisors assigned</Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', mt: 0.5 }}>Go to Sites page to assign supervisors</Typography>
            </Box>
          )}
        </Box>
      )}
      {/* ── Tab 4: Milestones ── */}
      {activeTab === 4 && (
        <MilestonesTab siteId={siteId} siteName={site.siteName} />
      )}
    </Box>
  );
};

export default SiteDetailsPage;